import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, Input, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  AddActionPointRequest,
  FlexiCompliance,
  FlexiComplianceActionPoint,
  FlexiComplianceActionPointStatus,
  FlexiComplianceLineItem,
  FlexiComplianceRating,
  FlexiComplianceResponsibleParty,
  FlexiComplianceRecurrenceType,
  FlexiComplianceHistoryItem,
  FlexiComplianceSection,
  FlexiComplianceStatus,
  FlexiComplianceLineSource,
  FLEXI_RECURRENCE_TYPES,
  recurrenceTypeKey,
  HasResponsibleParty,
  responsiblePartyNameSuffix
} from './flexi-compliance.models';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseServices } from '../../kuba.services';
import { complianceReportFileName, FlexiComplianceService } from './flexi-compliance.service';
import { FlexiComplianceTemplateService } from './templates/flexi-compliance-template.service';
import { EMOJI_ENTRY_ANIMATE, EMOJI_ENTRY_IDLE, flexiEmojiEntry } from '../animations/emoji-entry.animation';
import { FlexiEmojiEntryService } from '../services/flexi-emoji-entry.service';

/**
 * HSE-advisor authoring view for BHT-samsvar.
 *
 * Each section is rendered as a collapsible block. Each line item shows a
 * three-button rating (red/yellow/green), a comment textarea, and a small
 * "remove" affordance. The top of the page surfaces the live score
 * (percentage + smiley) so the advisor sees progress as they rate.
 *
 * Mutations call the BE one row at a time and update local state from the
 * response — score is recomputed server-side and returned in the aggregate.
 */
@Component({
  selector: 'flexi-compliance-rating',
  templateUrl: './compliance-rating.component.html',
  styleUrls: ['./flexi-compliance.scss'],
  animations: [flexiEmojiEntry],
  standalone: false
})
export class ComplianceRatingComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  @Input() aggregate!: FlexiCompliance;

  /** Template binding shortcuts (Angular can't access TS enums directly). */
  readonly Rating = FlexiComplianceRating;
  readonly Source = FlexiComplianceLineSource;
  readonly Status = FlexiComplianceStatus;
  readonly ApStatus = FlexiComplianceActionPointStatus;
  readonly Responsible = FlexiComplianceResponsibleParty;
  readonly Recurrence = FlexiComplianceRecurrenceType;
  readonly recurrenceTypes = FLEXI_RECURRENCE_TYPES;
  readonly recurrenceTypeKey = recurrenceTypeKey;

  /**
   * Per-line-item "Add action" form state. Keyed by lineItemId so multiple
   * forms can be open at once across collapsed/expanded sections.
   */
  addActionOpen = new Set<number>();
  addActionInput: Record<number, { title: string; description: string; responsible: FlexiComplianceResponsibleParty; due: string; recurrence: FlexiComplianceRecurrenceType; }> = {};

  /** Per-action-point busy latch for delete/status toggles. */
  busyActionIds = new Set<number>();

  /**
   * Per-action-point inline EDIT form state, keyed by action point id. Shared
   * by the attached (per-question) and loose lists — ids are unique across
   * both, so one map covers the page.
   */
  editActionOpen = new Set<number>();
  editActionInput: Record<number, { title: string; description: string; responsible: FlexiComplianceResponsibleParty; due: string; recurrence: FlexiComplianceRecurrenceType; }> = {};

  /**
   * "Follow-up actions" panel at the bottom of the page — loose action
   * points that belong to the assessment but are not pinned to a specific
   * line item (per dialog meeting pattern). State mirrors the per-item form
   * but lives at the component level.
   */
  looseActions: FlexiComplianceActionPoint[] = [];
  looseLoading = false;
  looseAddOpen = false;
  looseAddInput: {
    title: string; description: string; responsible: FlexiComplianceResponsibleParty;
    due: string; recurrence: FlexiComplianceRecurrenceType;
  } = {
    title: '', description: '', responsible: FlexiComplianceResponsibleParty.Customer,
    due: '', recurrence: FlexiComplianceRecurrenceType.None
  };
  looseError: string | null = null;

  /**
   * Accordion mode: only one section is open at a time. <c>activeSectionId</c>
   * is the one currently expanded. ngOnInit picks the first un-completed
   * section so the CSM lands on something actionable. When a section gets
   * fully rated we collapse it and auto-advance to the next un-completed one.
   */
  activeSectionId: number | null = null;

  /** Inline add-custom-item state, keyed by sectionId. */
  addCustomOpen = new Set<number>();
  addCustomInput: Record<number, { question: string; ref: string }> = {};

  /** Per-line-item busy flag so the trash / restore button can disable while in-flight. */
  busyLineItemIds = new Set<number>();

  /** Per-line-item local edits, so we can debounce comment saves to blur/save click. */
  commentDrafts: Record<number, string> = {};

  /** Show / hide soft-removed catalog items (off by default). */
  showRemoved = false;

  /**
   * A past review being previewed in place from the history strip, rendered
   * through the read-only final view rather than this page's editable controls.
   * Null means the live review is on screen.
   *
   * Reusing the final view is deliberate: it is already the renderer for an
   * archived review (see compliance-container), so "read-only" needs no new
   * rendering path and cannot drift from the one the customer sees.
   */
  peeked: FlexiCompliance | null = null;
  peekedOn: string | null = null;
  peekLoading = false;

  saving = false;
  downloading = false;
  regenerating = false;
  errorMessage: string | null = null;
  /**
   * Transient success banner — shown right after the user picks
   * "Save to Documents" and the regenerate POST returns successfully.
   * Cleared on the next user interaction so the banner doesn't linger.
   */
  successMessage: string | null = null;
  /**
   * Inline progress banner shown while the Complete call is generating
   * the PDF or while the user explicitly triggered a regenerate. Distinct
   * from <c>saving</c> so the body stays interactive for non-CSM users
   * peeking at progress (saving disables every input).
   */
  pdfBusyMessage: string | null = null;

  /** Latch for the Edit-Template button so double-clicks don't double-create. */
  ensuringTemplate = false;

  // ---------- "Start new compliance review" picker dialog ----------

  /** Modal open/close. */
  showStartNewDialog = false;
  /** Pickable templates (active, not deleted). Loaded lazily on dialog open. */
  pickerTemplates: { id: number; name: string; description: string | null; catalogVersionCode: string; isCurrentDefault: boolean; }[] = [];
  pickerLoading = false;
  pickerError: string | null = null;
  /** Bound to the radio selection. Null until user picks. */
  selectedTemplateId: number | null = null;
  /** Latch on the "Start" button so double-clicks don't start two reviews. */
  startingNewReview = false;

  /**
   * How many action points starting a new review will write off as "Avvist".
   * Null while loading, or when the probe failed — see the twin in
   * compliance-list.component.ts. The warning is simply omitted rather than
   * guessed at, and it never blocks starting the review.
   */
  outstandingActionPoints: number | null = null;
  outstandingLoading = false;

  constructor(
    private service: FlexiComplianceService,
    private templateService: FlexiComplianceTemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private emojiEntry: FlexiEmojiEntryService
  ) {}

  /**
   * Animation state for the score smiley. Resolved lazily and cached — the
   * one-time latch is consumed by whichever Flexi emoji renders first, and
   * re-reads on later change-detection passes must return the same value.
   * See FlexiEmojiEntryService.
   */
  private cachedEmojiEntryState: string | null = null;

  get emojiEntryState(): string {
    if (this.cachedEmojiEntryState === null) {
      this.cachedEmojiEntryState = this.emojiEntry.consume()
        ? EMOJI_ENTRY_ANIMATE
        : EMOJI_ENTRY_IDLE;
    }
    return this.cachedEmojiEntryState;
  }

  ngOnInit(): void {
    // Seed comment drafts from server state so editing is local until save.
    for (const section of this.aggregate.sections) {
      for (const item of section.items) {
        this.commentDrafts[item.id] = item.comment ?? '';
      }
    }
    // Always land with every section COLLAPSED, regardless of where the user
    // navigated from. Sections expand on demand (or auto-advance while rating);
    // nothing auto-expands on entry.
    this.activeSectionId = null;

    // Pull existing loose action points so the bottom panel renders them on
    // load. Filtered client-side from the flat list endpoint so we don't
    // need a dedicated "loose only" route on the BE.
    this.loadLooseActions();
  }

  private loadLooseActions(): void {
    this.looseLoading = true;
    this.looseError = null;
    this.service.listActionPoints(this.businessId).subscribe({
      next: rows => {
        this.looseActions = rows.filter(r => r.flexiComplianceLineItemId == null);
        this.looseLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.looseLoading = false;
        this.looseError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_ADD_FAILED');
      }
    });
  }

  // ---------- derived state ----------

  get businessId(): number { return this.aggregate.businessId; }
  get isCompleted(): boolean { return this.aggregate.status === FlexiComplianceStatus.Completed; }
  get score() { return this.aggregate.score; }

  /** Every active checkpoint across all sections carries a rating. */
  get allRated(): boolean {
    return ( this.aggregate.sections.length > 0
      && this.aggregate.sections.every(s => this.sectionRatingComplete(s)));
  }

  /**
   * Active checkpoints with no rating. Completing is still allowed, but they
   * score 0 and carry no action points, so nothing on the action plan can
   * raise them — the review can never reach 100%.
   */
  get unratedCount(): number {
    return this.aggregate.sections
      .reduce((sum, s) => sum + this.sectionTotalCount(s) - this.sectionRatedCount(s), 0);
  }

  /**
   * The review is OPEN (not Completed) yet every checkpoint is already rated —
   * i.e. it was reopened (or fully rated) but never closed. We surface a clear
   * banner so an open-but-finished review isn't mistaken for a completed one,
   * prompting the CSM to mark it Completed.
   */
  get isOpenAwaitingCompletion(): boolean {
    return !this.isCompleted && this.allRated;
  }

  /** Items in a section, hiding removed unless the CSM toggled "show removed". */
  visibleItems(section: FlexiComplianceSection): FlexiComplianceLineItem[] {
    return this.showRemoved ? section.items : section.items.filter(i => !i.isRemoved);
  }

  /** Accordion behavior: click toggles the active section. Click an open one to collapse all. */
  /**
   * Loads a past review for read-only preview, or returns to the live one.
   *
   * The editable content is hidden while previewing rather than disabled: a
   * snapshot cannot be rated, and greyed-out controls invite clicks that
   * cannot work.
   */
  onHistorySelected(row: FlexiComplianceHistoryItem | null): void {
    if (!row) {
      this.peeked = null;
      this.peekedOn = null;
      return;
    }
    this.peekLoading = true;
    this.errorMessage = null;
    this.service.getReview(this.businessId, row.id).subscribe({
      next: agg => {
        this.peeked = agg;
        this.peekedOn = row.completedOn;
        this.peekLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.peekLoading = false;
        this.errorMessage = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.LOAD_REVIEW_FAILED');
      }
    });
  }

  backToCurrent(): void {
    this.onHistorySelected(null);
  }

  toggleSection(sectionId: number): void {
    this.activeSectionId = this.activeSectionId === sectionId ? null : sectionId;
  }

  isOpen(sectionId: number): boolean {
    return this.activeSectionId === sectionId;
  }

  // ---------- per-section progress + tone ----------

  /** Active (not removed) items in a section — what counts toward "complete". */
  private activeItems(section: FlexiComplianceSection): FlexiComplianceLineItem[] {
    return section.items.filter(i => !i.isRemoved);
  }

  /** How many active items in a section have been rated (any rating). */
  sectionRatedCount(section: FlexiComplianceSection): number {
    return this.activeItems(section).filter(i => i.rating !== null && i.rating !== undefined).length;
  }

  sectionTotalCount(section: FlexiComplianceSection): number {
    return this.activeItems(section).length;
  }

  /**
   * Section colour reflects the WORST unresolved rating in the section,
   * not just how much is filled in:
   *   "red"         → at least one checkpoint is rated red (Missing) and
   *                   not yet resolved. Red dominates regardless of how many
   *                   green/yellow surround it.
   *   "yellow"      → no pending red, but at least one yellow
   *                   (NeedsImprovement).
   *   "green"       → every active checkpoint is rated green (Good). A
   *                   section only goes green when ALL of it is green.
   *   "in-progress" → partially rated, nothing red/yellow so far (the rated
   *                   ones are green but some are still unrated).
   *   "pending"     → nothing rated yet.
   * A red/yellow checkpoint is "resolved" by completing its action point,
   * which flips the rating to green (see auto-promotion on the BE) — so the
   * colour follows the current ratings directly.
   */
  sectionTone(section: FlexiComplianceSection): 'pending' | 'in-progress' | 'red' | 'yellow' | 'green' {
    const items = this.activeItems(section);
    if (items.length === 0) return 'green';       // empty section — nothing failing

    const rated = items.filter(i => i.rating !== null && i.rating !== undefined);
    if (rated.length === 0) return 'pending';

    if (rated.some(i => i.rating === this.Rating.Missing)) return 'red';
    if (rated.some(i => i.rating === this.Rating.NeedsImprovement)) return 'yellow';
    // Only green remains among the rated ones; green for the whole section
    // requires every item to be rated (and therefore green).
    return rated.length >= items.length ? 'green' : 'in-progress';
  }

  /**
   * Whether every active checkpoint in the section has a rating. Drives
   * auto-advance + initial landing — independent of the colour, since a
   * fully-rated red section is still "done rating" and we move past it.
   */
  private sectionRatingComplete(section: FlexiComplianceSection): boolean {
    const total = this.sectionTotalCount(section);
    return total === 0 || this.sectionRatedCount(section) >= total;
  }

  /** Percentage filled for the section's mini progress bar. */
  sectionProgressPercent(section: FlexiComplianceSection): number {
    const total = this.sectionTotalCount(section);
    if (total === 0) return 100;
    return Math.round((this.sectionRatedCount(section) / total) * 100);
  }

  /** First section that still has unrated items — used for auto-advance + initial state. */
  private firstIncompleteSectionId(): number | null {
    for (const s of this.aggregate.sections) {
      if (!this.sectionRatingComplete(s)) return s.id;
    }
    return null;
  }

  /** Next incomplete section AFTER the one currently active (wraps to start of list). */
  private nextIncompleteSectionId(currentId: number): number | null {
    const idx = this.aggregate.sections.findIndex(s => s.id === currentId);
    if (idx < 0) return this.firstIncompleteSectionId();
    // Look forward from idx+1, then wrap to before idx
    for (let i = idx + 1; i < this.aggregate.sections.length; i++) {
      if (!this.sectionRatingComplete(this.aggregate.sections[i])) return this.aggregate.sections[i].id;
    }
    for (let i = 0; i < idx; i++) {
      if (!this.sectionRatingComplete(this.aggregate.sections[i])) return this.aggregate.sections[i].id;
    }
    return null;
  }

  // ---------- rate ----------

  setRating(item: FlexiComplianceLineItem, rating: FlexiComplianceRating | null): void {
    if (this.isCompleted) return;
    // Snapshot the active section BEFORE we mutate state — we'll need it to
    // decide whether the section just completed and we should auto-advance.
    const sectionBefore = this.aggregate.sections.find(s => s.items.some(i => i.id === item.id));
    const wasCompleteBefore = sectionBefore ? this.sectionRatingComplete(sectionBefore) : false;

    this.busyLineItemIds.add(item.id);
    this.errorMessage = null;
    this.service.rateItem(this.businessId, item.id, rating, this.commentDrafts[item.id] ?? null).subscribe({
      next: updated => {
        item.rating = updated.rating;
        item.comment = updated.comment;
        this.busyLineItemIds.delete(item.id);
        // Auto-advance: if this rating just took the section from
        // in-progress/pending → completed, collapse it and open the next
        // incomplete section. Skip when the section was already complete
        // (e.g. user re-rated a single item) so we don't ping-pong the
        // accordion on every late edit.
        if (sectionBefore && !wasCompleteBefore && this.sectionRatingComplete(sectionBefore)) {
          const next = this.nextIncompleteSectionId(sectionBefore.id);
          this.activeSectionId = next;
        }
        // A red or yellow checkpoint owes the action plan a described action,
        // and the review cannot be completed until every one of them has one.
        // Opening the form here is what makes that a workflow rather than a
        // wall the advisor hits at the end with no idea which item caused it.
        if (this.needsAction(item)) this.openAddAction(item);
        // Refetch for the new score (server-computed); preserves any local
        // edits via applyAggregate's draft merge.
        this.refresh();
      },
      error: (err: HttpErrorResponse) => {
        this.busyLineItemIds.delete(item.id);
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.RATE_FAILED');
      }
    });
  }

  /** Save comment for an item without changing the rating. Called on blur. */
  saveComment(item: FlexiComplianceLineItem): void {
    if (this.isCompleted) return;
    const draft = this.commentDrafts[item.id] ?? '';
    if (draft === (item.comment ?? '')) return; // no-op
    this.setRating(item, item.rating); // re-uses the same endpoint
  }

  // ---------- add custom item ----------

  openAddCustom(sectionId: number): void {
    this.addCustomOpen.add(sectionId);
    this.addCustomInput[sectionId] = { question: '', ref: '' };
  }
  cancelAddCustom(sectionId: number): void { this.addCustomOpen.delete(sectionId); }

  submitAddCustom(sectionId: number): void {
    const input = this.addCustomInput[sectionId];
    const q = (input?.question || '').trim();
    if (!q || this.saving) return;
    this.saving = true;
    this.errorMessage = null;
    this.service.addCustomItem(this.businessId, sectionId, {
      question: q,
      regulatoryReference: (input.ref || '').trim() || null
    }).subscribe({
      next: agg => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.applyAggregate(agg);
        this.addCustomOpen.delete(sectionId);
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ADD_FAILED');
      }
    });
  }

  // ---------- action points ----------

  /**
   * Whether this checkpoint still owes the action plan an action.
   *
   * Mirrors the server's completion gate exactly — red or yellow, and no
   * non-rejected action point. A rejected one is the record of work that will
   * NOT happen, so it does not discharge the obligation. The description is
   * optional: a titled action is enough.
   */
  needsAction(item: FlexiComplianceLineItem): boolean {
    if (item.isRemoved) return false;
    if (item.rating !== FlexiComplianceRating.Missing
        && item.rating !== FlexiComplianceRating.NeedsImprovement) return false;
    return !(item.actionPoints || []).some(a =>
      a.status !== FlexiComplianceActionPointStatus.Rejected);
  }

  /** Every checkpoint blocking completion, in the order they are displayed. */
  get checkpointsMissingAction(): FlexiComplianceLineItem[] {
    return this.aggregate.sections
      .flatMap(s => s.items)
      .filter(i => this.needsAction(i));
  }

  /** Opens the inline form to add a new action point under <c>item</c>. */
  openAddAction(item: FlexiComplianceLineItem): void {
    if (this.isCompleted) return;
    this.addActionOpen.add(item.id);
    this.addActionInput[item.id] = {
      title: '',
      description: '',
      responsible: FlexiComplianceResponsibleParty.Customer,
      due: '',
      recurrence: FlexiComplianceRecurrenceType.None
    };
  }
  cancelAddAction(itemId: number): void { this.addActionOpen.delete(itemId); }

  /** Submits the inline form for <c>item</c> and appends the new action. */
  submitAddAction(item: FlexiComplianceLineItem): void {
    const input = this.addActionInput[item.id];
    const title = (input?.title || '').trim();
    if (!title || this.saving) return;
    this.saving = true;
    this.errorMessage = null;
    const due = (input.due || '').trim();
    // A recurring action point anchors its series on its own deadline, so the
    // server requires one. Caught here to keep the message next to the field.
    if (input.recurrence !== FlexiComplianceRecurrenceType.None && !due) {
      this.saving = false;
      this.errorMessage = this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.DEADLINE_REQUIRED');
      return;
    }
    const body: AddActionPointRequest = {
      title,
      description: (input.description || '').trim() || null,
      responsibleParty: input.responsible,
      dueDate: due || null,
      recurrenceType: input.recurrence
    };
    this.service.addActionPoint(this.businessId, item.id, body).subscribe({
      next: created => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        // Append to the line item in-place so we don't re-fetch the whole aggregate.
        item.actionPoints = [...(item.actionPoints || []), created];
        this.addActionOpen.delete(item.id);
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_ADD_FAILED');
      }
    });
  }

  // ---------- action points: edit / delete gating ----------

  /**
   * The window in which an action point can be edited OR deleted: it has to
   * still be outstanding, on a review that is itself still open.
   *
   * A completed action point records what was agreed AND delivered, so its
   * wording, owner, and deadline freeze (the BE answers 409) and it can no
   * longer be removed — deleting the whole review is the only way out, and
   * that cascades over completed ones deliberately. Re-open the action point
   * to edit it again.
   */
  isActionOutstanding(action: FlexiComplianceActionPoint): boolean {
    return ( !this.isCompleted
      && action.status !== FlexiComplianceActionPointStatus.Completed
    );
  }

  /** Opens the inline edit form, seeded from the action point's current values. */
  openEditAction(action: FlexiComplianceActionPoint): void {
    if (!this.isActionOutstanding(action) || this.busyActionIds.has(action.id)) return;
    this.editActionOpen.add(action.id);
    this.editActionInput[action.id] = {
      title: action.title,
      description: action.description ?? '',
      responsible: action.responsibleParty ?? FlexiComplianceResponsibleParty.Customer,
      due: action.dueDate ?? '',
      recurrence: action.recurrenceType ?? FlexiComplianceRecurrenceType.None
    };
  }

  cancelEditAction(actionId: number): void { this.editActionOpen.delete(actionId); }

  /**
   * Saves the inline edit. <paramref name="item"/> is the parent question for
   * an attached action point, or omitted for a loose one — that's the only
   * difference between the two lists, so both share this path.
   */
  submitEditAction(action: FlexiComplianceActionPoint, item?: FlexiComplianceLineItem): void {
    const input = this.editActionInput[action.id];
    const title = (input?.title || '').trim();
    if (!title || this.busyActionIds.has(action.id)) return;
    const due = (input.due || '').trim();
    this.busyActionIds.add(action.id);
    this.errorMessage = null;
    if (input.recurrence !== FlexiComplianceRecurrenceType.None && !due) {
      this.busyActionIds.delete(action.id);
      this.errorMessage = this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.DEADLINE_REQUIRED');
      return;
    }
    this.service.updateActionPoint(this.businessId, action.id, {
      title,
      // Empty string clears the description server-side.
      description: (input.description || '').trim(),
      responsibleParty: input.responsible,
      dueDate: due || null,
      // A null dueDate means "unchanged", so an emptied field needs the flag.
      clearDueDate: !due,
      recurrenceType: input.recurrence
    }).subscribe({
      next: updated => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        if (item) {
          item.actionPoints = item.actionPoints.map(a => a.id === updated.id ? updated : a);
        } else {
          this.looseActions = this.looseActions.map(a => a.id === updated.id ? updated : a);
        }
        this.editActionOpen.delete(action.id);
        this.busyActionIds.delete(action.id);
      },
      error: (err: HttpErrorResponse) => {
        this.busyActionIds.delete(action.id);
        this.errorMessage = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_UPDATE_FAILED');
      }
    });
  }

  // ---------- Edit template ----------

  /**
   * Resolves the business's default template (creating one from the
   * IsCurrent catalog if none exists) and navigates to its editor. Visible
   * to CSM/HSE-advisor only — Editor/Guest never sees the rating page so
   * the button is implicitly hidden for them.
   */
  editTemplate(): void {
    if (this.ensuringTemplate) return;
    this.ensuringTemplate = true;
    this.errorMessage = null;
    this.templateService.ensureDefaultForBusiness(this.businessId).subscribe({
      next: template => {
        this.ensuringTemplate = false;
        // Anchor on this.route.parent (the `compliance` route) rather than
        // this.route (the empty-path child) — '..' from the empty-path
        // child would strip 'compliance' off the URL and land at
        // Flexi/templates, which doesn't exist.
        // businessId rides along as the copy-on-write context: edits to a
        // template shared with other businesses fork it for THIS business
        // instead of silently changing everyone's.
        this.router.navigate(['templates', template.id], {
          relativeTo: this.route.parent,
          queryParams: { businessId: this.businessId }
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ensuringTemplate = false;
        this.errorMessage = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.RATE_FAILED');
      }
    });
  }

  /**
   * Jump straight to the global template list — entry point for creating a
   * new template, switching this business's default to a different one, or
   * just browsing what's available. Sibling to <c>editTemplate()</c>, which
   * shortcuts into the current default's editor.
   */
  browseTemplates(): void {
    this.router.navigate(['templates'], { relativeTo: this.route.parent });
  }

  // ---------- Start new compliance review ----------

  /**
   * Open the picker dialog and load the available templates. Loads on
   * each open so a CSM who created a template in another tab sees it
   * without a hard refresh.
   */
  openStartNewReview(): void {
    if (!this.isCompleted) return;
    this.showStartNewDialog = true;
    this.pickerError = null;
    this.selectedTemplateId = this.aggregate.templateId ?? null;
    this.loadPickerTemplates();
    this.loadOutstandingActionPoints();
  }

  /**
   * Counts the commitments the sweep will reject, so the dialog can say so
   * before the user agrees to it. Errors are swallowed: the number is
   * advisory, and failing to read it must not stop a review being started.
   */
  private loadOutstandingActionPoints(): void {
    this.outstandingActionPoints = null;
    this.outstandingLoading = true;
    this.service.countOutstandingActionPoints(this.businessId).subscribe({
      next: count => {
        this.outstandingActionPoints = count;
        this.outstandingLoading = false;
      },
      error: () => {
        this.outstandingActionPoints = null;
        this.outstandingLoading = false;
      }
    });
  }

  closeStartNewReview(): void {
    if (this.startingNewReview) return;
    this.showStartNewDialog = false;
  }

  /**
   * Jumps straight into the 4-step creation wizard. After saving there the
   * user lands in the new template's editor; coming back here, the picker
   * reloads its template list on open so the fresh one shows up.
   */
  goCreateTemplate(): void {
    this.showStartNewDialog = false;
    this.router.navigate(['templates', 'new'], { relativeTo: this.route.parent });
  }

  /**
   * Back to the history grid. The rating view sits one segment deeper, so we
   * navigate to the parent rather than pushing a new route.
   */
  backToList(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  private loadPickerTemplates(): void {
    this.pickerLoading = true;
    // Scoped to this business: its own templates plus unclaimed library ones.
    // See compliance-list.component.ts — an unscoped list leaked the first
    // customer's named template into every other business's picker.
    this.templateService.list(false, this.businessId).subscribe({
      next: rows => {
        const currentDefaultId = this.aggregate.templateId ?? null;
        // Active only. Show the business's current default first so the
        // CSM doesn't have to hunt.
        this.pickerTemplates = (rows || [])
          .filter(r => r.isActive && !r.isDeleted)
          .map(r => ({
            id: r.id,
            name: r.name,
            description: r.description ?? null,
            catalogVersionCode: r.catalogVersionCode,
            isCurrentDefault: r.id === currentDefaultId
          }))
          .sort((a, b) => {
            if (a.isCurrentDefault && !b.isCurrentDefault) return -1;
            if (b.isCurrentDefault && !a.isCurrentDefault) return 1;
            return a.name.localeCompare(b.name);
          });

        // An empty library is a dead end, not an empty state: confirmStartNewReview
        // refuses to run without a selectedTemplateId, so the dialog would offer
        // nothing to pick and the button would sit inert with no explanation. That
        // is the normal situation for a business whose compliance tab was just
        // enabled — templates are created lazily, and nothing has created one yet.
        //
        // So create it here, the same way the Edit-template button does:
        // ensure-default seeds a per-business template from the current catalogue
        // and assigns it. Idempotent, so a second open finds the existing one.
        if (this.pickerTemplates.length === 0) {
          this.ensureTemplateForEmptyPicker();
          return;
        }

        this.pickerLoading = false;
        this.preselectPickerTemplate();
      },
      error: (err: HttpErrorResponse) => {
        this.pickerLoading = false;
        this.pickerError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.START_NEW.LOAD_FAILED');
      }
    });
  }

  /**
   * Creates the business's default template when the library is empty, then
   * puts it in the picker and selects it.
   *
   * Deliberately reuses ensure-default rather than asking the CSM to go and
   * press Edit template first: both produce the same per-business template, and
   * requiring a detour through another screen to make a primary button work is
   * not a workflow anyone would choose.
   */
  private ensureTemplateForEmptyPicker(): void {
    this.templateService.ensureDefaultForBusiness(this.businessId).subscribe({
      next: template => {
        this.pickerTemplates = [{
          id: template.id,
          name: template.name,
          description: template.description ?? null,
          catalogVersionCode: template.catalogVersionCode,
          isCurrentDefault: true
        }];
        this.pickerLoading = false;
        this.preselectPickerTemplate();
      },
      // Leaves the picker empty rather than pretending. The message says what
      // failed, so the CSM is not left staring at an inert button.
      error: (err: HttpErrorResponse) => {
        this.pickerLoading = false;
        this.pickerError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.START_NEW.LOAD_FAILED');
      }
    });
  }

  /**
   * Makes sure something is selected once the list is known.
   *
   * openStartNewReview seeds the selection from the current review's templateId,
   * which is null for every review seeded straight from the catalogue — so
   * without this the dialog opens with a populated list and nothing chosen, and
   * the confirm button still does nothing.
   */
  private preselectPickerTemplate(): void {
    const stillListed = this.selectedTemplateId !== null
      && this.pickerTemplates.some(t => t.id === this.selectedTemplateId);
    if (stillListed) return;

    const preferred = this.pickerTemplates.find(t => t.isCurrentDefault)
      ?? this.pickerTemplates[0];
    this.selectedTemplateId = preferred ? preferred.id : null;
  }

  /**
   * Confirms the picker, supersedes the current assessment, seeds a fresh
   * one from the chosen template, and reloads the aggregate so the CSM
   * lands in the rating view of the new assessment.
   */
  confirmStartNewReview(): void {
    if (this.startingNewReview || this.selectedTemplateId == null) return;
    this.startingNewReview = true;
    this.pickerError = null;
    this.service.startNewReview(this.businessId, this.selectedTemplateId).subscribe({
      next: aggregate => {
        this.startingNewReview = false;
        this.showStartNewDialog = false;
        // The new review is a different record, so the ':id' in the URL has to
        // follow it — otherwise a refresh would reload the superseded one.
        this.router.navigate(['../', aggregate.id], { relativeTo: this.route });
        // Replace the bound aggregate so the rating view immediately
        // reflects the new assessment (Draft, no ratings, all items fresh).
        this.aggregate = aggregate;
        this.commentDrafts = {};
        for (const section of this.aggregate.sections) {
          for (const item of section.items) {
            this.commentDrafts[item.id] = item.comment ?? '';
          }
        }
        this.activeSectionId = this.firstIncompleteSectionId() ?? this.aggregate.sections[0]?.id ?? null;
        this.successMessage = this.translate.instant('FLEXI_COMPLIANCE.START_NEW.STARTED');
        this.loadLooseActions();
      },
      error: (err: HttpErrorResponse) => {
        this.startingNewReview = false;
        this.pickerError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.START_NEW.FAILED');
      }
    });
  }

  // ---------- Loose action points (bottom panel) ----------

  openLooseAdd(): void {
    if (this.isCompleted) return;
    this.looseAddOpen = true;
    this.looseAddInput = {
      title: '', description: '', responsible: FlexiComplianceResponsibleParty.Customer,
      due: '', recurrence: FlexiComplianceRecurrenceType.None
    };
  }
  cancelLooseAdd(): void { this.looseAddOpen = false; }

  submitLooseAdd(): void {
    const title = (this.looseAddInput.title || '').trim();
    if (!title || this.saving) return;
    this.saving = true;
    this.looseError = null;
    const looseDue = (this.looseAddInput.due || '').trim();
    if (this.looseAddInput.recurrence !== FlexiComplianceRecurrenceType.None && !looseDue) {
      this.saving = false;
      this.looseError = this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.DEADLINE_REQUIRED');
      return;
    }
    const body: AddActionPointRequest = {
      title,
      description: (this.looseAddInput.description || '').trim() || null,
      responsibleParty: this.looseAddInput.responsible,
      dueDate: looseDue || null,
      recurrenceType: this.looseAddInput.recurrence
    };
    this.service.addLooseActionPoint(this.businessId, body).subscribe({
      next: created => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.looseActions = [created, ...this.looseActions];
        this.looseAddOpen = false;
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.looseError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_ADD_FAILED');
      }
    });
  }

  toggleLooseComplete(action: FlexiComplianceActionPoint): void {
    if (this.isCompleted || this.busyActionIds.has(action.id)) return;
    const nextStatus = action.status === FlexiComplianceActionPointStatus.Completed
      ? FlexiComplianceActionPointStatus.Open
      : FlexiComplianceActionPointStatus.Completed;
    this.busyActionIds.add(action.id);
    this.service.updateActionPoint(this.businessId, action.id, { status: nextStatus }).subscribe({
      next: updated => {
        this.looseActions = this.looseActions.map(a => a.id === updated.id ? updated : a);
        this.busyActionIds.delete(action.id);
      },
      error: (err: HttpErrorResponse) => {
        this.busyActionIds.delete(action.id);
        this.looseError = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_UPDATE_FAILED');
      }
    });
  }

  deleteLooseAction(action: FlexiComplianceActionPoint): void {
    if (!this.isActionOutstanding(action) || this.busyActionIds.has(action.id)) return;
    this.confirmationService.confirm({
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.DELETE_ACTION'),
      accept: () => {
        this.busyActionIds.add(action.id);
        this.service.deleteActionPoint(this.businessId, action.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            this.looseActions = this.looseActions.filter(a => a.id !== action.id);
            this.busyActionIds.delete(action.id);
          },
          error: (err: HttpErrorResponse) => {
            this.busyActionIds.delete(action.id);
            this.looseError = err.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_DELETE_FAILED');
          }
        });
      }
    });
  }

  /** Toggles the action point between Open and Completed via the small check button. */
  toggleActionComplete(item: FlexiComplianceLineItem, action: FlexiComplianceActionPoint): void {
    if (this.isCompleted || this.busyActionIds.has(action.id)) return;
    const nextStatus = action.status === FlexiComplianceActionPointStatus.Completed
      ? FlexiComplianceActionPointStatus.Open
      : FlexiComplianceActionPointStatus.Completed;
    this.busyActionIds.add(action.id);
    this.service.updateActionPoint(this.businessId, action.id, { status: nextStatus }).subscribe({
      next: updated => {
        // Patch in place so the UI updates without losing scroll.
        item.actionPoints = item.actionPoints.map(a => a.id === updated.id ? updated : a);
        this.busyActionIds.delete(action.id);
      },
      error: (err: HttpErrorResponse) => {
        this.busyActionIds.delete(action.id);
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_UPDATE_FAILED');
      }
    });
  }

  /** Soft-deletes an action point with a confirm prompt. Completed ones are
   *  not deletable — the button is hidden and this is the matching guard. */
  deleteAction(item: FlexiComplianceLineItem, action: FlexiComplianceActionPoint): void {
    if (!this.isActionOutstanding(action) || this.busyActionIds.has(action.id)) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.DELETE_ACTION'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.busyActionIds.add(action.id);
        this.service.deleteActionPoint(this.businessId, action.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            item.actionPoints = item.actionPoints.filter(a => a.id !== action.id);
            this.busyActionIds.delete(action.id);
          },
          error: (err: HttpErrorResponse) => {
            this.busyActionIds.delete(action.id);
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_DELETE_FAILED');
          }
        });
      }
    });
  }

  /** Helper for the responsible-party chip label. */
  /**
   * " - Kari Nordmann" for the responsible chip. Appended to the translated
   * role so a chip names the person and not only the side. Empty when the role
   * has no holder — see responsiblePartyNameSuffix.
   */
  responsibleNameSuffix(action: HasResponsibleParty): string {
    return responsiblePartyNameSuffix(action);
  }

  responsibleLabelKey(party: FlexiComplianceResponsibleParty | null): string {
    return party === FlexiComplianceResponsibleParty.Advisor
      ? 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR'
      : 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER';
  }

  // ---------- remove / restore ----------

  removeItem(item: FlexiComplianceLineItem): void {
    if (this.isCompleted || this.busyLineItemIds.has(item.id)) return;
    const messageKey = item.sourceType === FlexiComplianceLineSource.Custom
      ? 'FLEXI_COMPLIANCE.CONFIRM.REMOVE_CUSTOM'
      : 'FLEXI_COMPLIANCE.CONFIRM.REMOVE_CATALOG';
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant(messageKey),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.busyLineItemIds.add(item.id);
        this.service.removeItem(this.businessId, item.id).subscribe({
          next: agg => { this.applyAggregate(agg); this.busyLineItemIds.delete(item.id); },
          error: (err: HttpErrorResponse) => {
            this.busyLineItemIds.delete(item.id);
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.REMOVE_FAILED');
          }
        });
      }
    });
  }

  restoreItem(item: FlexiComplianceLineItem): void {
    if (this.isCompleted || this.busyLineItemIds.has(item.id)) return;
    this.busyLineItemIds.add(item.id);
    this.service.restoreItem(this.businessId, item.id).subscribe({
      next: agg => { this.applyAggregate(agg); this.busyLineItemIds.delete(item.id); },
      error: (err: HttpErrorResponse) => {
        this.busyLineItemIds.delete(item.id);
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.RESTORE_FAILED');
      }
    });
  }

  // ---------- complete / reopen ----------

  complete(): void {
    if (this.isCompleted || this.saving) return;

    // The server refuses this too, but its message cannot say WHICH checkpoints
    // are short — only the client holds the whole tree. Naming them, and
    // jumping to the first one, turns a dead end into the next step.
    const blocking = this.checkpointsMissingAction;
    if (blocking.length > 0) {
      const first = blocking[0];
      this.activeSectionId = this.aggregate.sections
        .find(s => s.items.some(i => i.id === first.id))?.id ?? this.activeSectionId;
      this.openAddAction(first);
      this.errorMessage = this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_REQUIRED', {
        count: blocking.length,
        items: blocking.map(i => i.question).join(', ')
      });
      return;
    }

    // Unrated checkpoints do not block completion, but the advisor should
    // know before confirming that they cap the score below 100%.
    const unrated = this.unratedCount;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: unrated > 0
        ? this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.COMPLETE_UNRATED', { count: unrated })
        : this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.COMPLETE'),
      icon: unrated > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.saving = true;
        this.errorMessage = null;
        this.successMessage = null;
        // Complete on the BE wraps "save status + generate + archive PDF"
        // in one transaction. Surface that to the user so they don't think
        // the app froze during a 2-3 second iTextSharp render.
        this.pdfBusyMessage = this.translate.instant('FLEXI_COMPLIANCE.PDF_PROGRESS.COMPLETING');
        this.service.complete(this.businessId).subscribe({
          next: agg => {
            this.applyAggregate(agg);
            // Collapse every section once the review is completed — the
            // completed state should read as a clean, closed summary.
            this.activeSectionId = null;
            this.saving = false;
            this.pdfBusyMessage = null;
            this.successMessage = this.translate.instant('FLEXI_COMPLIANCE.PDF_PROGRESS.COMPLETED');
          },
          error: (err: HttpErrorResponse) => {
            this.saving = false;
            this.pdfBusyMessage = null;
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.COMPLETE_FAILED');
          }
        });
      }
    });
  }

  reopen(): void {
    if (!this.isCompleted || this.saving) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.REOPEN'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.saving = true;
        this.errorMessage = null;
        this.service.reopen(this.businessId).subscribe({
          // Land collapsed after reopening; the open-awaiting-completion
          // banner makes the reopened state clear without auto-expanding.
          next: agg => { this.applyAggregate(agg); this.activeSectionId = null; this.saving = false; },
          error: (err: HttpErrorResponse) => {
            this.saving = false;
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.REOPEN_FAILED');
          }
        });
      }
    });
  }

  // ---------- PDF: open chooser dialog → regenerate OR download ----------

  /**
   * Entry point bound to the toolbar "Download as PDF" button.
   * Shows a two-action confirmation dialog asking whether to:
   *   (1) regenerate the PDF and replace the archived copy in
   *       Flexi → Documents → OHS-plans, or
   *   (2) just download a copy of the currently archived blob.
   * The CSM picks; we then invoke the corresponding flow with explicit
   * progress + success / error feedback.
   */
  openPdfChooser(): void {
    if (!this.isCompleted || this.downloading || this.regenerating) return;
    this.errorMessage = null;
    this.successMessage = null;
    this.confirmationService.confirm({
      header: this.translate.instant('FLEXI_COMPLIANCE.PDF_CHOOSER.TITLE'),
      message: this.translate.instant('FLEXI_COMPLIANCE.PDF_CHOOSER.MESSAGE'),
      icon: 'pi pi-file-pdf',
      acceptLabel: this.translate.instant('FLEXI_COMPLIANCE.PDF_CHOOSER.SAVE_TO_DOCS'),
      rejectLabel: this.translate.instant('FLEXI_COMPLIANCE.PDF_CHOOSER.DOWNLOAD_COPY'),
      acceptIcon: 'pi pi-cloud-upload',
      rejectIcon: 'pi pi-download',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-outlined',
      // Accept = "save fresh PDF to Documents". Reject = "just download".
      // PrimeNG's confirm doesn't support 3 actions natively, so we map
      // Cancel through the close icon (defaultFocus prevents accidental enter).
      defaultFocus: 'accept',
      accept: () => this.regenerateAndArchive(),
      reject: (type: any) => {
        // type === 1 (REJECT) → user clicked Download a copy
        // type === 2 (CANCEL) → user clicked X / pressed Esc → do nothing
        if (type === 1) this.downloadCurrentPdf();
      }
    });
  }

  /**
   * Re-runs the BE generator (POST /regenerate-pdf), which uploads a new
   * blob, supersedes the prior archived row, and stamps
   * GeneratedFeatureManualDocumentId. Shows an inline progress banner
   * while it's in flight and a transient success banner on completion.
   */
  private regenerateAndArchive(): void {
    if (this.regenerating) return;
    this.regenerating = true;
    this.pdfBusyMessage = this.translate.instant('FLEXI_COMPLIANCE.PDF_PROGRESS.REGENERATING');
    this.service.regenerateReportPdf(this.businessId).subscribe({
      next: () => {
        this.regenerating = false;
        this.pdfBusyMessage = null;
        this.successMessage = this.translate.instant('FLEXI_COMPLIANCE.PDF_PROGRESS.SAVED_TO_DOCS');
      },
      error: (err: HttpErrorResponse) => {
        this.regenerating = false;
        this.pdfBusyMessage = null;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.REGENERATE_FAILED');
      }
    });
  }

  /**
   * Streams the currently archived PDF blob and triggers a browser
   * download. Same source as the doc-tree link in Flexi → Documents →
   * OHS-plans. Returns a localized error if the blob is missing (Azure
   * retention / manual cleanup) — the user can then pick the
   * "Save to Documents" option to regenerate.
   */
  private downloadCurrentPdf(): void {
    if (this.downloading) return;
    this.downloading = true;
    this.service.downloadReportPdf(this.businessId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = complianceReportFileName(
          this.translate.instant('FLEXI_COMPLIANCE.REPORT_FILE_NAME'),
          BaseServices.BusinessName,
          this.aggregate?.completedOn,
        );
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.downloading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.downloading = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.PDF_FAILED');
      }
    });
  }

  /** Old method name kept as an alias — the template binds to the new chooser. */
  downloadReportPdf(): void { this.openPdfChooser(); }

  // ---------- helpers ----------

  smileyClass(): string {
    return `is-${this.score.smiley}`;
  }

  /**
   * Smiley image asset matching the score bucket. Kept in sync with the
   * advisor-card chip and the customer final view so the same face appears
   * in all three places.
   */
  smileyImage(): string {
    switch (this.score.smiley) {
      case 'green':  return 'assets/images/happy-smile.png';
      case 'yellow': return 'assets/images/neutral-smile.png';
      case 'red':    return 'assets/images/sad-smile.png';
      default:       return 'assets/images/sad-smile.png';
    }
  }

  private refresh(): void {
    this.service.getOrCreate(this.businessId).subscribe({
      next: agg => this.applyAggregate(agg),
      error: () => { /* non-blocking */ }
    });
  }

  private applyAggregate(agg: FlexiCompliance): void {
    // Preserve the comment drafts the user was editing while merging in
    // the fresh aggregate from the server.
    const pendingDrafts = { ...this.commentDrafts };
    this.aggregate = agg;
    for (const section of agg.sections) {
      for (const item of section.items) {
        if (pendingDrafts[item.id] !== undefined && pendingDrafts[item.id] !== (item.comment ?? '')) {
          this.commentDrafts[item.id] = pendingDrafts[item.id];
        } else {
          this.commentDrafts[item.id] = item.comment ?? '';
        }
      }
    }
  }
}
